import * as Sentry from "@sentry/react";

/**
 * Chrome/Edge page translation swaps or wraps text nodes React still holds.
 * React's next removeChild/insertBefore against such a node throws
 * NotFoundError and takes the app down. Tolerate the mismatch instead.
 * See https://github.com/facebook/react/issues/11538.
 */
const INSTALLED = Symbol.for("cohesive:translation-dom-guard");

function report(method: string, node: Node): void {
  const message = `${method}: node is not a child of this node`;
  console.warn(`[translation-dom-guard] ${message}`, node);
  Sentry.addBreadcrumb({
    category: "translation-dom-guard",
    level: "warning",
    message,
  });
}

export function installTranslationDomGuard(): void {
  if (typeof Node === "undefined") {
    return;
  }
  const proto = Node.prototype as Node & { [INSTALLED]?: true };
  if (proto[INSTALLED]) {
    return;
  }
  proto[INSTALLED] = true;

  const removeChild = proto.removeChild;
  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      report("removeChild", child);
      return child;
    }
    return removeChild.call(this, child) as T;
  };

  const insertBefore = proto.insertBefore;
  proto.insertBefore = function <T extends Node>(
    this: Node,
    node: T,
    child: Node | null,
  ): T {
    if (child && child.parentNode !== this) {
      report("insertBefore", child);
      // Append rather than drop: React now treats `node` as a child of this
      // node, so it must really be one for later updates to find it.
      return this.appendChild(node);
    }
    return insertBefore.call(this, node, child) as T;
  };
}
